/**
 * Layout component with minimal brutalist navigation
 */

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (user) {
      apiClient.getUserProfile()
        .then(profile => setSubscriptionStatus(profile.subscription_status))
        .catch(() => setSubscriptionStatus(null));
    } else {
      setSubscriptionStatus(null);
    }
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/signin');
    setMobileMenuOpen(false);
  };

  const isActive = (path: string) => location.pathname === path;
  const showUpgradeLink = user && (subscriptionStatus === 'free' || subscriptionStatus === null);

  return (
    <div className="min-h-screen bg-bg-primary">
      {/* Top Navigation - Thin, Minimal */}
      <nav className="sticky top-0 z-50 border-b border-border-dim bg-bg-primary">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          {/* Logo */}
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2 md:gap-3 group">
            <img
              src="/logo-primary.svg"
              alt="PermitLoop"
              className="h-6 md:h-8 w-6 md:w-8 transition-opacity group-hover:opacity-70"
            />
            <span className="text-lg md:text-xl font-bold tracking-tight">PermitLoop</span>
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center gap-8">
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className={`text-sm font-medium transition-colors ${
                    isActive('/dashboard')
                      ? 'text-white'
                      : 'text-text-secondary hover:text-white'
                  }`}
                >
                  Dashboard
                </Link>
                {showUpgradeLink && (
                  <Link
                    to="/pricing"
                    className={`text-sm font-medium transition-colors ${
                      isActive('/pricing')
                        ? 'text-white'
                        : 'text-text-secondary hover:text-white'
                    }`}
                  >
                    Upgrade
                  </Link>
                )}
                <button
                  onClick={handleSignOut}
                  className="text-sm font-medium text-text-secondary hover:text-white transition-colors"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/pricing"
                  className="text-sm font-medium text-text-secondary hover:text-white transition-colors"
                >
                  Pricing
                </Link>
                <Link
                  to="/signin"
                  className="text-sm font-medium text-text-secondary hover:text-white transition-colors"
                >
                  Sign In
                </Link>
                <Link to="/signup" className="btn-primary text-sm py-2 px-6">
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-white hover:text-text-secondary transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border-dim bg-bg-primary">
            <div className="px-4 py-3 space-y-3">
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block py-2 text-sm font-medium transition-colors ${
                      isActive('/dashboard')
                        ? 'text-white'
                        : 'text-text-secondary hover:text-white'
                    }`}
                  >
                    Dashboard
                  </Link>
                  {showUpgradeLink && (
                    <Link
                      to="/pricing"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`block py-2 text-sm font-medium transition-colors ${
                        isActive('/pricing')
                          ? 'text-white'
                          : 'text-text-secondary hover:text-white'
                      }`}
                    >
                      Upgrade
                    </Link>
                  )}
                  <button
                    onClick={handleSignOut}
                    className="block w-full text-left py-2 text-sm font-medium text-text-secondary hover:text-white transition-colors"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/pricing"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block py-2 text-sm font-medium text-text-secondary hover:text-white transition-colors"
                  >
                    Pricing
                  </Link>
                  <Link
                    to="/signin"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block py-2 text-sm font-medium text-text-secondary hover:text-white transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block btn-primary text-sm py-2 px-4 text-center"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer - Minimal */}
      <footer className="border-t border-border-dim mt-12 md:mt-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-xs md:text-sm text-text-muted font-mono">
              © 2026 Spot Labs Inc. All rights reserved.
            </div>
            <div className="flex items-center gap-4 md:gap-6">
              <a
                href="/terms"
                className="text-xs md:text-sm text-text-muted hover:text-text-secondary transition-colors"
              >
                Terms
              </a>
              <a
                href="/privacy"
                className="text-xs md:text-sm text-text-muted hover:text-text-secondary transition-colors"
              >
                Privacy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
